import { REVALIDATE_TIME } from "@/constants/cache"
import { API_PREFIX_TAMDA_GROUP_SETTING_PATH } from "@/constants/prefix-path-api"
import httpClient from "@/lib/http-client"
import {
  TamdaGroupEuSettingListResponse,
  TamdaGroupEuSettingListResponseDataItemLocalized,
  TamdaGroupEuSettingResponse,
  TamdaGroupEuSettingResponseDataObject,
} from "@/types/codegen/data-contracts"

const FALLBACK_FIELDS = ["googleMapUrl", "facebookUrl", "twitterUrl", "youtubeUrl", "email", "address", "hotline", "copyright"] as const

export const fetchTamdaGroupSetting = async (locale: string): Promise<TamdaGroupEuSettingResponseDataObject | null> => {
  try {
    // Public settings must stay anonymous. The authenticated HttpClient adds
    // `Authorization` from localStorage, which makes Strapi's Redis cache
    // plugin deliberately bypass this otherwise public GET.
    const response = await httpClient.get<TamdaGroupEuSettingResponse | TamdaGroupEuSettingListResponse>(
      API_PREFIX_TAMDA_GROUP_SETTING_PATH,
      { locale, populate: "*" },
      {
        next: {
          revalidate: REVALIDATE_TIME,
          tags: [`tamda_group_setting_${locale}`],
        },
      } as RequestInit,
    )

    const data = Array.isArray((response as TamdaGroupEuSettingListResponse)?.data)
      ? (response as TamdaGroupEuSettingListResponse).data?.[0]
      : (response as TamdaGroupEuSettingResponse)?.data

    if (!data?.attributes) return data ?? null

    const attrs = data.attributes as Record<string, any>
    const viLocale = attrs.localizations?.data?.find((locale: TamdaGroupEuSettingListResponseDataItemLocalized) => locale.attributes?.locale === "vi")
    const viAttrs = viLocale?.attributes

    if (viAttrs) {
      for (const field of FALLBACK_FIELDS) {
        const val = attrs[field]
        if (val == null || val === "") attrs[field] = viAttrs[field] ?? val
      }
    }

    return data
  } catch (error) {
    console.error("Failed to fetch tamda group setting:", error)
    return null
  }
}

export const fetchTamdaOneScriptSettings = async (): Promise<{
  headerScript?: string | null
  bodyScript?: string | null
  GoogleAnalyticsTrackingID?: string | null
} | null> => {
  try {
    const response = await httpClient.get(
      API_PREFIX_TAMDA_GROUP_SETTING_PATH,
      { fields: ["headerScript", "bodyScript", "GoogleAnalyticsTrackingID"] },
      { next: { revalidate: REVALIDATE_TIME, tags: ["tamdaone_scripts"] } } as RequestInit,
    )
    const data = Array.isArray((response as any)?.data) ? (response as any).data[0] : (response as any)?.data
    return data?.attributes ?? null
  } catch (error) {
    console.error("Failed to fetch tamda one script settings:", error)
    return null
  }
}
